class DreambabyAdminRequestHandler {
    static giftlistDomain = '';
    static proxyToken = '';

    constructor() {
        this.giftlistDomain = DreambabyAdminRequestHandler.normalizeDomain((window.DreambabyConfig && window.DreambabyConfig.giftlistDomain) || DreambabyAdminRequestHandler.giftlistDomain);
        this.proxyToken = (window.DreambabyConfig && window.DreambabyConfig.proxyToken) || DreambabyAdminRequestHandler.proxyToken;
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            'Authorization': this.proxyToken ? `Bearer ${this.proxyToken}` : undefined
        };
    }

    static normalizeDomain(domain) {
        return (domain || '').trim().replace(/\/+$/, '');
    }

    /**
     * @param {string} action - The action endpoint (e.g., 'customer-add-tag')
     * @param {Object} payload - The request body data
     * @param {Object} options - Additional request options
     * @returns {Promise<Object>} - Response data
     */
    async buildRequest(action, payload, options = {}) {
        if (!this.giftlistDomain || !this.proxyToken) {
            throw new Error('Missing giftlistDomain or proxyToken');
        }

        const endpoint = `${this.giftlistDomain}/proxy/shopify-admin/${action}?authorization=fixed`;
        
        const requestOptions = {
            method: options.method || 'POST',
            headers: {
                ...this.defaultHeaders,
                ...options.headers
            },
            body: JSON.stringify(payload)
        };

        const response = await fetch(endpoint, requestOptions);
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Request failed with status ${response.status}: ${errorText}`);
        }

        return await this.parseResponse(response, action);
    }

    async parseResponse(response, action) {
        const responseText = await response.text();
        
        if (!responseText?.trim()) {
            console.warn(`Empty response from ${action}, treating as success`);
            return { success: true };
        }
        
        try {
            return JSON.parse(responseText);
        } catch (parseError) {
            throw new Error(`Invalid JSON response: ${parseError.message}`);
        }
    }

    /**
     * @param {string} customerId - Customer ID
     * @param {string} tag - Tag to add
     * @returns {Promise<Object>}
     */
    async addCustomerTag(customerId, tag) {
        return this.buildRequest('customer-add-tag', {
            customerId,
            tagToAdd: tag
        });
    }

    /**
     * @param {string} customerId - Customer ID
     * @param {string} tag - Tag to remove
     * @returns {Promise<Object>}
     */
    async removeCustomerTag(customerId, tag) {
        return this.buildRequest('customer-remove-tag', {
            customerId,
            tagToRemove: tag
        });
    }

    /**
     * @param {string} customerId - Customer ID
     * @param {string} metaField - Metafield as namespace.key (e.g. 'custom.vermoedelijke_geboortedatum')
     * @param {string} content - Metafield value
     * @returns {Promise<Object>}
     */
    async setCustomerMetafield(customerId, metaField, content) {
        return this.buildRequest('customer-set-metafield', {
            customerId,
            content,
            MetaField: metaField
        });
    }

    /**
     * @param {string} customerEmail - Customer email
     * @param {boolean} acceptsLoyalty - Loyalty consent
     * @param {boolean} acceptsMarketing - Marketing consent
     * @returns {Promise<Object>}
     */
    async updateCustomerConsent(customerEmail, acceptsLoyalty, acceptsMarketing) {
        return this.buildRequest('customer-consent', {
            customerEmail,
            acceptsLoyalty,
            acceptsMarketing
        });
    }

    /**
     * @param {string} customerEmail - Customer email
     * @returns {Promise<{exists: false} | {exists: true, loyalty: boolean, marketing: boolean}>}
     */
    async getCustomerConsentStatus(customerEmail) {
        if (!this.giftlistDomain || !this.proxyToken) {
            throw new Error('Missing giftlistDomain or proxyToken');
        }

        const endpoint = `${this.giftlistDomain}/proxy/shopify-admin/customer-consent-status?authorization=fixed`;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: this.defaultHeaders,
            body: JSON.stringify({ customerEmail })
        });

        if (response.status === 404) {
            return { exists: false };
        }

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Request failed with status ${response.status}: ${errorText}`);
        }

        const responseText = await response.text();
        if (!responseText?.trim()) {
            return { exists: true, loyalty: false, marketing: false };
        }

        try {
            const data = JSON.parse(responseText);
            return {
                exists: true,
                loyalty: !!data.loyalty,
                marketing: !!data.marketing
            };
        } catch (parseError) {
            throw new Error(`Invalid JSON response: ${parseError.message}`);
        }
    }

    /**
     * @param {Object} subscriptionData - Subscription data
     * @returns {Promise<Object>}
     */
    async subscribeLoyalty(subscriptionData) {
        return this.buildRequest('customer-subscribe-loyalty', subscriptionData);
    }

    /**
     * @param {Object} inventoryData - Inventory request data
     * @returns {Promise<Object>}
     */
    async getInventory(inventoryData) {
        return this.buildRequest('inventory', inventoryData);
    }
}
